/**
 * ImmobilierLuxePremiumTemplate — Cinematic, exclusive, timeless.
 * Design spec: T01 — sells the dream, not the square footage.
 * Palette: obsidian (#0A0A0A) + gold (#D4AF37) + white
 * Fonts: Cormorant Garamond (display) + Manrope (body)
 */
import EditableText from "./EditableText";
import type { WebsiteCustomizations } from "./PremiumTemplate";

interface Props {
  name: string;
  desc: string;
  product: string;
  city: string;
  customizations?: WebsiteCustomizations;
  editMode?: boolean;
  onCustomize?: (field: keyof WebsiteCustomizations, value: string) => void;
}

const c = (f: keyof WebsiteCustomizations, fb: string, cu?: WebsiteCustomizations) => cu?.[f] || fb;

const ImmobilierLuxePremiumTemplate = ({ name, desc, city, customizations, editMode, onCustomize }: Props) => {
  const set = (f: keyof WebsiteCustomizations) => (v: string) => onCustomize?.(f, v);
  const heroTitle = c("heroTitle", name || "Maisons d'exception", customizations);
  const heroSub = c("heroSubtitle", desc || "Une collection confidentielle de biens rares, pour une clientèle qui n'a pas le temps d'attendre.", customizations);

  return (
    <div
      className="min-h-screen relative"
      style={{
        background: "#0A0A0A",
        color: "#FFFFFF",
        fontFamily: "'Manrope', sans-serif",
      }}
      data-testid="template-immobilier-luxe-premium"
    >
      {/* Noise texture overlay */}
      <div
        className="fixed inset-0 opacity-[0.035] pointer-events-none z-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Gold vertical grid lines */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.06]">
        <div className="max-w-[1600px] h-full mx-auto grid grid-cols-12 gap-0">
          {Array.from({ length: 13 }).map((_, i) => (
            <div key={i} className="border-l border-[#D4AF37] h-full" />
          ))}
        </div>
      </div>

      {/* Top nav */}
      <header className="relative z-20 px-8 md:px-16 py-8 flex items-center justify-between">
        <EditableText
          as="div"
          value={name || "Belleville & Fils"}
          onChange={set("logoText")}
          editMode={editMode}
          data-field="logoText"
          className="tracking-[0.35em] uppercase"
          style={{ fontSize: "13px", fontWeight: 500, color: "#D4AF37" }}
        />
        <nav className="hidden md:flex items-center gap-10 text-xs tracking-[0.25em] uppercase opacity-80">
          <span className="hover:text-[#D4AF37] cursor-pointer transition-colors">Collection</span>
          <span className="hover:text-[#D4AF37] cursor-pointer transition-colors">Services</span>
          <span className="hover:text-[#D4AF37] cursor-pointer transition-colors">Contact</span>
        </nav>
        <div className="text-xs tracking-[0.25em] uppercase opacity-60 hidden md:block">{city || "Paris"}</div>
      </header>

      {/* Hero — split asynchronous */}
      <section className="relative z-20 px-8 md:px-16 pt-12 md:pt-20 pb-24 md:pb-32">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-7 md:sticky md:top-20">
            <div className="text-xs tracking-[0.4em] uppercase mb-8" style={{ color: "#D4AF37" }}>
              — Maisons d'exception
            </div>
            <EditableText
              as="h1"
              value={heroTitle}
              onChange={set("heroTitle")}
              editMode={editMode}
              data-field="heroTitle"
              className="leading-[0.92] mb-10"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(3.5rem, 8vw, 7.5rem)",
                fontWeight: 400,
                letterSpacing: "-0.02em",
              }}
            />
            <EditableText
              as="p"
              value={heroSub}
              onChange={set("heroSubtitle")}
              editMode={editMode}
              data-field="heroSubtitle"
              multiline
              className="text-lg md:text-xl max-w-xl leading-[1.6] opacity-80"
              style={{ fontWeight: 300, fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif" }}
            />
            <div className="mt-12 flex items-center gap-6">
              <EditableText
                as="button"
                value={c("ctaText", "Découvrir la collection", customizations)}
                onChange={set("ctaText")}
                editMode={editMode}
                data-field="ctaText"
                className="px-10 py-4 border border-[#D4AF37] transition-all hover:bg-[#D4AF37] hover:text-[#0A0A0A] group"
                style={{
                  color: "#D4AF37",
                  fontSize: "12px",
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                  fontWeight: 500,
                }}
              />
              <div className="text-xs tracking-[0.2em] uppercase opacity-50">Prise de rendez-vous privée →</div>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="aspect-[3/4] relative overflow-hidden">
              <div
                className="absolute inset-0"
                style={{
                  background: "linear-gradient(180deg, rgba(212,175,55,0.08) 0%, #1A1A1A 60%, #0A0A0A 100%)",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center p-12">
                <div className="text-center">
                  <div className="text-[13px] tracking-[0.4em] uppercase mb-6" style={{ color: "#D4AF37" }}>
                    Hôtel particulier
                  </div>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "72px", fontWeight: 300, lineHeight: 0.9 }}>
                    VII<sup style={{ fontSize: "32px" }}>e</sup>
                  </div>
                  <div className="mt-6 text-sm opacity-70">Paris · 480 m²</div>
                  <div className="mt-2 text-xs tracking-[0.3em] uppercase opacity-50">Sur demande</div>
                </div>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs">
                <span className="tracking-[0.25em] uppercase opacity-60">01 / 09</span>
                <span className="tracking-[0.25em] uppercase opacity-60">↓ Scroll</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee — discreet partners */}
      <section className="relative z-20 border-y border-[#D4AF37]/20 py-8 overflow-hidden">
        <div className="flex items-center gap-16 animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex items-center gap-16">
              {["Sotheby's", "Christie's", "Barnes", "Émile Garcin", "Daniel Féau", "Coldwell Banker"].map((p, i) => (
                <span
                  key={`${dup}-${i}`}
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "26px", fontWeight: 400 }}
                  className="opacity-40 tracking-wide italic"
                >
                  {p}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Bento grid — featured properties */}
      <section className="relative z-20 px-8 md:px-16 py-24">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-baseline justify-between mb-16">
            <div>
              <div className="text-xs tracking-[0.4em] uppercase mb-3" style={{ color: "#D4AF37" }}>
                — 02
              </div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)", fontWeight: 400, letterSpacing: "-0.01em" }}>
                Sélection d'exception
              </h2>
            </div>
            <div className="hidden md:block text-xs tracking-[0.3em] uppercase opacity-60">Voir tout ↗</div>
          </div>

          <div className="grid md:grid-cols-3 grid-rows-2 gap-4 md:gap-6 h-auto md:h-[720px]">
            <div className="md:col-span-2 md:row-span-2 relative group overflow-hidden cursor-pointer">
              <div className="absolute inset-0 transition-transform duration-1000 group-hover:scale-105" style={{ background: "linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%)" }} />
              <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-between">
                <div className="text-[11px] tracking-[0.4em] uppercase" style={{ color: "#D4AF37" }}>Monument historique · Île Saint-Louis</div>
                <div>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)", fontWeight: 300, lineHeight: 1 }}>
                    Hôtel Lambert<br /><em>Reloaded</em>
                  </div>
                  <div className="flex items-center justify-between mt-6 text-sm opacity-70">
                    <span>1 200 m² · 7 chambres</span>
                    <span style={{ color: "#D4AF37" }}>Sur demande</span>
                  </div>
                </div>
              </div>
            </div>
            {[
              { sub: "Rive gauche · 420 m²", title: "Maison d'Architecte", price: "14,8 M€" },
              { sub: "Bord de Seine · 280 m²", title: "Duplex sur Cour", price: "9,5 M€" },
            ].map((p, i) => (
              <div key={i} className="relative group overflow-hidden cursor-pointer" style={{ background: "#1A1A1A" }}>
                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                  <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: "#D4AF37" }}>{p.sub}</div>
                  <div>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400, lineHeight: 1 }}>
                      {p.title}
                    </div>
                    <div className="flex items-center justify-between mt-3 text-xs opacity-70">
                      <span>→ Fiche détaillée</span>
                      <span style={{ color: "#D4AF37" }}>{p.price}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cinematic quote */}
      <section className="relative z-20 px-8 md:px-16 py-32 border-t border-[#D4AF37]/15">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-xs tracking-[0.4em] uppercase mb-10 opacity-60">— Témoignage</div>
          <EditableText
            as="blockquote"
            value={c("testimonial", "Ils ne vendent pas des biens. Ils vendent un art de vivre, avec la discrétion d'un ami de famille.", customizations)}
            onChange={set("testimonial")}
            editMode={editMode}
            data-field="testimonial"
            multiline
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(1.8rem, 3.5vw, 3rem)",
              fontWeight: 300,
              fontStyle: "italic",
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
          />
          <div className="mt-10 flex items-center justify-center gap-4 text-xs tracking-[0.3em] uppercase opacity-70">
            <span className="w-8 h-px bg-[#D4AF37]" />
            <span>Famille S., Neuilly</span>
            <span className="w-8 h-px bg-[#D4AF37]" />
          </div>
        </div>
      </section>

      {/* Footer — massive logo */}
      <footer className="relative z-20 px-8 md:px-16 py-20 border-t border-[#D4AF37]/30">
        <div className="max-w-[1600px] mx-auto">
          <div
            className="mb-16 leading-none opacity-95"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(4rem, 14vw, 16rem)",
              fontWeight: 300,
              letterSpacing: "-0.03em",
              color: "#D4AF37",
            }}
          >
            {(name || "Belleville").split(" ")[0]}
          </div>
          <div className="grid md:grid-cols-4 gap-8 pt-10 border-t border-[#D4AF37]/20 text-sm">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase opacity-50 mb-3">Paris</div>
              <div>{c("phone", "01 42 22 15 00", customizations)}</div>
              <div className="opacity-70 mt-1">{c("email", "contact@belleville.immo", customizations)}</div>
            </div>
            <div>
              <div className="text-xs tracking-[0.3em] uppercase opacity-50 mb-3">Adresse</div>
              <div className="opacity-70">12 place Vendôme<br />75001 Paris</div>
            </div>
            <div>
              <div className="text-xs tracking-[0.3em] uppercase opacity-50 mb-3">Horaires</div>
              <div className="opacity-70">Sur rendez-vous<br />Lun.—Sam. · 10h—19h</div>
            </div>
            <div>
              <div className="text-xs tracking-[0.3em] uppercase opacity-50 mb-3">Éthique</div>
              <div className="opacity-70 italic">« La discrétion avant tout. »</div>
            </div>
          </div>
          <div className="mt-12 text-xs opacity-40 tracking-[0.25em] uppercase">© 2026 · Tous droits réservés</div>
        </div>
      </footer>

      <style>{`
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 45s linear infinite; }
      `}</style>
    </div>
  );
};

export default ImmobilierLuxePremiumTemplate;
